import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Unsubscribe } from "firebase/auth";
import { collection, onSnapshot } from "firebase/firestore";

import { firestore } from "../core";
import { useAuth } from "../core/providers/auth";

interface UsersContext {
  loading: boolean;
  error: string | null;
  users: PublicUser[];
  usersExt: PublicUserExtended[];
}

export interface PublicUserExtended extends PublicUser {
  isAdmin: boolean;
  isCoach: boolean;
  credits: number;
  photoURL: string;
}

export interface PublicUser {
  uid: string;
  displayName: string;
  email: string;
  phoneNumber: string;
}

const Users = createContext({} as UsersContext);

export default function UsersProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [usersExt, setUsersExt] = useState<PublicUserExtended[]>([]);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    let unsub: Unsubscribe;
    let q;
    setLoading(true);

    q = collection(firestore, "/users");

    setUsers([]);
    setUsersExt([]);

    unsub = onSnapshot(
      q,
      (snapshot) => {
        const usersExt: PublicUserExtended[] = [];
        const users: PublicUser[] = [];
        snapshot.forEach((docs) => {
          users.push({ 
            uid: docs.id, 
            displayName: docs.data().displayName,
            email: docs.data().email,
            phoneNumber: docs.data().phoneNumber 
          } as PublicUser);
          usersExt.push({
            uid: docs.id, 
            displayName: docs.data().displayName,
            email: docs.data().email,
            phoneNumber: docs.data().phoneNumber,
            isAdmin: docs.data().isAdmin,
            isCoach: docs.data().isCoach,
            credits: docs.data().credits,
            photoURL: docs.data().photoURL
          } as PublicUserExtended);
        });

        setUsersExt(usersExt);
        setUsers(users);
        setError("")
        setLoading(false);
      },
      (error) => {
        setError(error.message);
        setLoading(false);
      }
    );

    return () => {
      unsub && unsub();
      setUsersExt([]);
      setUsers([]);
    };
  }, [user]);

  return (
    <Users.Provider
      value={{
        loading,
        error,
        users,
        usersExt
      }}
    >
      {children}
    </Users.Provider>
  );
}

export const useUsers = () => useContext(Users);